// src/components/navSections.js
//
// Reorganized nav taxonomy — see specs/20260422-drawer-taxonomy/plan.md.
// Opt-in via localStorage `caro.portal.newNav === '1'`. When the flag is
// unset, Layout.js keeps rendering the original `menuItems` +
// `bottomMenuGroups` structure, so this can ship ahead of any broad
// rollout without disturbing existing users.
//
// The factory takes an auth/permission context so each item's
// visibility can be evaluated exactly as the old structure did.

import React from 'react';
import {
  Dashboard as DashboardIcon,
  Lightbulb as LightbulbIcon,
  LocalHospital as AlarmIcon,
  BugReport as TicketsIcon,
  Notifications as NotificationsIcon,
  Videocam as VideocamIcon,
  Engineering as EngineeringIcon,
  LocationOn as LocationsIcon,
  People as PeopleIcon,
  Security as UserManagementIcon,
  Cloud as DevicesIcon,
  Devices as DeviceTypeIcon,
  SystemUpdate as FirmwareIcon,
  Link as LinkIcon,
  RadioButtonChecked as ButtonIcon,
  Assignment as InspectionIcon,
  SmartToy as AutomationIcon,
  PowerSettingsNew as PowerIcon,
  AutoFixHigh as BulkNameIcon,
  Phone as PhoneIcon,
  Router as RouterIcon,
  Map as MapIcon,
  TabletMac as EinkIcon,
  IntegrationInstructions as IntegrationIcon,
  Business as BusinessIcon,
  NotificationsActive as NotificationsActiveIcon,
  Category as CategoryIcon,
  Apps as AppsIcon,
  Monitor as MonitorIcon,
  Troubleshoot as TroubleshootIcon,
  Storefront as StorefrontIcon,
  RequestQuote as RequestQuoteIcon,
  Handshake as HandshakeIcon,
  Inventory2 as CatalogManagementIcon,
  Code as CodeIcon,
  Help as HelpIcon,
  MarkEmailUnread as QuarantineIcon,
  Visibility as MonitorSectionIcon,
  FolderShared as DirectoryIcon,
  Memory as HardwareIcon,
  Hub as AutomationSectionIcon,
  CorporateFare as OrganizationIcon,
  Event as EventIcon,
  Celebration as CelebrationIcon,
  Settings as SystemSectionIcon,
  Storage as DatabaseIcon,
  ElectricMeter as EnergyMeteringIcon,
} from '@mui/icons-material';

/**
 * Build the taxonomy. Returns an array of sections; each section has
 * `items: [{ text, path, icon, adminOnly?, requiresPermission? }]`.
 *
 * The shape matches what Layout's existing rendering logic expects for
 * groups — no renderer changes required.
 */
export function buildNewNavSections({ t, locationTenancyPermission, permissionTemplatesPermission, cameraRequestsPermission, fieldInterventionsPermission, canManageUsers, canViewUsers, companyManagementPermission, knowledgeBaseManagePermission, shopPermission, catalogManagePermission, estimatesPermission, ideasPermission, documentsPermission, documentsGrantsPermission, documentsExclusionsPermission, documentsRetentionPermission, documentsArchiveBadge, monitoringViewPermission, dbHealthPermission, invoicingViewPermission, invoicingConfigPermission, invoicingPaymentsPermission, posCatalogPermission, posRestrictionsPermission, posTerminalsPermission, posDayClosePermission, posReportsPermission, modulesManagePermission, modulesViewPermission, organisationPermission, onboardingViewPermission, supplierPhase = 0, meteringViewPermission = false }) {
  const tr = (key, fallback) => (t ? t(key) : fallback);

  return [
    {
      id: 'monitor',
      label: tr('Nav.monitor', 'Monitor'),
      icon: <MonitorSectionIcon />,
      defaultOpen: true,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_dashboard', 'Dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
        // No adminOnly: alarms are day-to-day work for ordinary staff, not an admin
        // tool. Gating on role === 'admin' hid the page from every user who actually
        // holds alarms.view. Visibility now comes from PAGE_REGISTRY via isItemVisible.
        { text: tr('Layout.menu_alarms', 'Alarms'), icon: <AlarmIcon />, path: '/alarms' },
        { text: tr('Layout.menu_tickets', 'Tickets'), icon: <TicketsIcon />, path: '/tickets' },
        { text: tr('Layout.menu_ticket_quarantine', 'E-mail Quarantine'), icon: <QuarantineIcon />, path: '/tickets/quarantine' },
        { text: tr('Layout.menu_notifications', 'Notifications'), icon: <NotificationsIcon />, path: '/notifications' },
        { text: tr('Layout.menu_camera_requests', 'Camera Requests'), icon: <VideocamIcon />, path: '/camera-requests', requiresPermission: cameraRequestsPermission },
        { text: tr('Layout.menu_field_interventions', 'Field Interventions'), icon: <EngineeringIcon />, path: '/field-interventions', requiresPermission: fieldInterventionsPermission },
      ],
    },
    {
      id: 'directory',
      label: tr('Nav.directory', 'Directory'),
      icon: <DirectoryIcon />,
      defaultOpen: true,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_locations', 'Locations'), icon: <LocationsIcon />, path: '/locations' },
        { text: tr('Layout.menu_persons', 'Persons'), icon: <PeopleIcon />, path: '/persons' },
        // Agenda en Activiteiten stonden alleen in het klassieke menu (spec
        // 20260825-animatie-activiteiten-en-agenda §8). Hier bij Personen, want ze gaan
        // over het dagelijks leven van de bewoners en niet over het bewaken van de
        // installatie.
        //
        // Geen `requiresPermission`: PAGE_REGISTRY beslist (/agenda -> agenda.view,
        // /activities -> activities.view). `agenda.view` zit in geen enkele rolstandaard
        // en in geen enkel sjabloon, dus deze ingang blijft onzichtbaar tot iemand de
        // sleutel bewust toekent. Activiteiten is bovendien een BETALENDE module; die
        // poort zit in de pagina zelf, want "jij mist een recht" en "jullie hebben dit
        // niet gekocht" zijn twee verschillende gesprekken.
        { text: tr('Layout.menu_agenda', 'Agenda'), icon: <EventIcon />, path: '/agenda' },
        { text: tr('Layout.menu_activities', 'Activities'), icon: <CelebrationIcon />, path: '/activities' },
        // Documentclassificatie (spec 20260902-documentclassificatie, T-5a.35 / PD-9). The registry
        // row for /documents names seven keys (classify + six topic readers); `documentsPermission`
        // is "any of them", computed in Layout.js. Reading a file is the engine's answer, never a
        // key (D-14) — this entry only decides whether the work list is in the menu.
        { text: tr('Layout.menu_documents', 'Documenten'), icon: <DirectoryIcon />, path: '/documents', requiresPermission: documentsPermission },
        // 5b (T-5b.19): the two PROTECTED admin pages. `hasPermission` in Layout.js honours
        // PROTECTED_PERMISSIONS, so a company_admin without the explicit grant sees neither entry —
        // which matches what the API will tell them. The pair is incompatible (§7.4): one account
        // legitimately sees at most one of the two.
        { text: tr('Layout.menu_documents_grants', 'Documenttoegang'), icon: <DirectoryIcon />, path: '/documents/grants', requiresPermission: documentsGrantsPermission },
        { text: tr('Layout.menu_documents_exclusions', 'Documentuitsluitingen'), icon: <DirectoryIcon />, path: '/documents/exclusions', requiresPermission: documentsExclusionsPermission },
        // 5c (T-5c.14): the archiving page, `documents.retention` (PROTECTED). `badge` is the number of
        // documents past their proposed term, fetched in Layout.js ONLY when the caller holds the key —
        // an account without it makes no call to /archive/badge (H4.2).
        { text: tr('Layout.menu_documents_archive', 'Archivering'), icon: <DirectoryIcon />, path: '/documents/archive', requiresPermission: documentsRetentionPermission, badge: documentsArchiveBadge || null, badgeColor: 'error' },
        // `canViewUsers` = users.view OR users.manage OR users.permissions.manage, the same
        // three keys PAGE_REGISTRY declares for /users. Naming only the matrix right here
        // hid the page from everyone an administrator had granted `users.manage`
        // (spec 20260826-company-user-management LD-3).
        { text: tr('Layout.menu_user_management', 'User management'), icon: <UserManagementIcon />, path: '/users', requiresPermission: canViewUsers },
        // Deep-links to the Templates tab of /users. Same treatment as
        // /catalog-management -> Shop: a real route, so the nav can highlight it.
        { text: tr('Layout.menu_permission_templates', 'Permission templates'), icon: <UserManagementIcon />, path: '/permission-templates', requiresPermission: permissionTemplatesPermission },
        // Cross-tenant platform hygiene, PROTECTED key. Sits next to user management
        // because it is the same kind of question: who may see whose records.
        { text: tr('Layout.menu_location_tenancy', 'Tenant assignment'), icon: <UserManagementIcon />, path: '/admin/location-tenancy', requiresPermission: locationTenancyPermission },
        // No requiresPermission: isItemVisible() reads the gate straight from
        // PAGE_REGISTRY (/audit -> audit.view). One source, so the menu and the route
        // cannot disagree.
        { text: tr('Layout.menu_audit', 'Audit trail'), icon: <TroubleshootIcon />, path: '/audit' },
      ],
    },
    {
      id: 'hardware',
      label: tr('Nav.hardware', 'Hardware'),
      icon: <HardwareIcon />,
      defaultOpen: false,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_devices', 'Devices'), icon: <DevicesIcon />, path: '/devices' },
        { text: tr('Layout.menu_device_health', 'Device Health'), icon: <MonitorIcon />, path: '/devices/health' },
        // Camera's (spec 20260831-cameras-als-toestel). De zichtbaarheid komt uit
        // PAGE_REGISTRY (`/cameras` → `cameras.view`), mét `adminOnly` erbovenop:
        // camerabeheer gaat over RTSP-adressen, inloggegevens van klantapparatuur en wie
        // mag meekijken. Beslissing van de eigenaar op 2026-09-03 — CARO-werk, niet voor
        // gewone gebruikers. Zie de uitgebreide noot in Layout.js, inclusief wat dit voor
        // `company_admin` betekent.
        { text: tr('Layout.menu_cameras', "Camera's"), icon: <VideocamIcon />, path: '/cameras', adminOnly: true },
        { text: tr('Layout.menu_tablet_permissions', 'Tablet Permissions'), icon: <DeviceTypeIcon />, path: '/tablet-permissions', adminOnly: true },
        { text: tr('Layout.menu_device_type_management', 'Device Type Management'), icon: <DeviceTypeIcon />, path: '/device-type-management', adminOnly: true },
        { text: tr('Layout.menu_device_linking', 'Device Linking'), icon: <LinkIcon />, path: '/device-linking', adminOnly: true },
        { text: tr('Layout.menu_firmware_management', 'Firmware Management'), icon: <FirmwareIcon />, path: '/firmware-management', adminOnly: true },
        { text: tr('Layout.menu_enocean_buttons', 'EnOcean Buttons'), icon: <ButtonIcon />, path: '/enocean-buttons', adminOnly: true },
        { text: tr('Layout.menu_enocean_import', 'EnOcean Import'), icon: <ButtonIcon />, path: '/enocean-import', adminOnly: true },
        // Shop — the device catalog, reachable from every role's nav (decision
        // 2026-07-17). Mirrors the old `menuItems` entries so flipping
        // `caro.portal.newNav` doesn't drop the Shop. View-only by construction;
        // prices inside are gated separately on catalog.view_prices.
        { text: tr('Layout.menu_shop', 'Shop'), icon: <StorefrontIcon />, path: '/shop', requiresPermission: shopPermission },
        { text: tr('Layout.menu_catalog_management', 'Catalog Management'), icon: <CatalogManagementIcon />, path: '/catalog-management', requiresPermission: catalogManagePermission },
        { text: tr('Layout.menu_estimates', 'Estimates'), icon: <RequestQuoteIcon />, path: '/estimates', requiresPermission: estimatesPermission },
        // Leveranciers (20260930-leveranciers-tickets-interventies, PD-2). No
        // requiresPermission: isItemVisible() derives suppliers.view from PAGE_REGISTRY.
        // `featureHidden` is the extra `&& phase >= 1` — SUPPLIERS_ENABLED off means 404.
        { text: tr('suppliers.menu', 'Leveranciers'), icon: <HandshakeIcon />, path: '/leveranciers', featureHidden: !(supplierPhase >= 1) },
      ],
    },
    // ── Paid modules (spec 20260818-invoicingsystem) ───────────────────────
    //
    // Their own sections rather than ten more items inside 'hardware', where they buried
    // the rest of the menu. Mirrors the two groups in Layout.js: the drawer-taxonomy file
    // and the legacy list are TWO nav definitions and both have to be edited (EF-15).
    //
    // Every flag already folds in the module check, so a customer without the module sees
    // no section at all rather than one that 403s on every entry.
    {
      id: 'invoicing',
      label: tr('Layout.group_invoicing', 'Invoicing'),
      icon: <RequestQuoteIcon />,
      defaultOpen: false,
      items: [
        { text: tr('Layout.menu_invoicing', 'Invoices'), icon: <RequestQuoteIcon />, path: '/invoicing', requiresPermission: invoicingViewPermission },
        { text: tr('Layout.menu_invoicing_items', 'To be invoiced'), icon: <RequestQuoteIcon />, path: '/invoicing/items', requiresPermission: invoicingViewPermission },
        { text: tr('Layout.menu_invoicing_payments', 'Payments'), icon: <RequestQuoteIcon />, path: '/invoicing/payments', requiresPermission: invoicingPaymentsPermission },
        { text: tr('Layout.menu_invoicing_config', 'Settings'), icon: <RequestQuoteIcon />, path: '/invoicing/config', requiresPermission: invoicingConfigPermission },
      ],
    },
    {
      id: 'cafeteria',
      label: tr('Layout.group_cafeteria', 'POS'),
      icon: <StorefrontIcon />,
      defaultOpen: false,
      items: [
        { text: tr('Layout.menu_pos_catalog', 'Catalog'), icon: <StorefrontIcon />, path: '/pos/catalog', requiresPermission: posCatalogPermission },
        { text: tr('Layout.menu_pos_config', 'Till settings'), icon: <StorefrontIcon />, path: '/pos/config', requiresPermission: posCatalogPermission },
        { text: tr('Layout.menu_pos_restrictions', 'Product restrictions'), icon: <StorefrontIcon />, path: '/pos/restrictions', requiresPermission: posRestrictionsPermission },
        { text: tr('Layout.menu_pos_terminals', 'Till terminals'), icon: <StorefrontIcon />, path: '/pos/terminals', requiresPermission: posTerminalsPermission },
        { text: tr('Layout.menu_pos_day_closes', 'Day closes'), icon: <StorefrontIcon />, path: '/pos/day-closes', requiresPermission: posDayClosePermission },
        { text: tr('Layout.menu_pos_reports', 'POS report'), icon: <StorefrontIcon />, path: '/pos/reports', requiresPermission: posReportsPermission },
      ],
    },
    // Plaatsbeschrijvingen (betalende module `inspections`, spec
    // 20260901-bewoner-onboarding). Spiegelt de groep in Layout.js: dit bestand en de
    // klassieke lijst zijn TWEE menudefinities en allebei moeten ze bijgewerkt worden
    // (EF-15). Geen `requiresPermission` — `isItemVisible()` haalt de sleutel uit
    // PAGE_REGISTRY, en de modulepoort zit in de pagina zelf.
    {
      id: 'inspections',
      label: tr('Layout.group_inspections', 'Condition reports'),
      icon: <InspectionIcon />,
      defaultOpen: false,
      items: [
        { text: tr('Layout.menu_inspections', 'Overview'), icon: <InspectionIcon />, path: '/inspections' },
        { text: tr('Layout.menu_inspection_templates', 'Templates'), icon: <InspectionIcon />, path: '/inspections/templates' },
      ],
    },
    // Energiemeting (betalende module `metering`, specs/20261001-mbus-portal-integration).
    // Spiegelt de groep in Layout.js. De vlag bevat permissie EN module (canRead), zodat het
    // menu verborgen blijft zolang de module nooit actief was (plan §10.1).
    {
      id: 'energy_metering',
      label: tr('Layout.group_energy_metering', 'Energiemeting'),
      icon: <EnergyMeteringIcon />,
      defaultOpen: false,
      items: [
        { text: tr('Layout.menu_energy_metering', 'Energiemeting'), icon: <EnergyMeteringIcon />, path: '/energy-metering', requiresPermission: meteringViewPermission },
      ],
    },
    {
      id: 'automation',
      label: tr('Nav.automation', 'Automation & Integrations'),
      icon: <AutomationSectionIcon />,
      defaultOpen: false,
      adminOnly: true,
      items: [
        { text: tr('Layout.menu_automations', 'Automations'), icon: <AutomationIcon />, path: '/automations', adminOnly: true },
        { text: tr('Layout.menu_qbus_output_types', 'Qbus Output Types'), icon: <PowerIcon />, path: '/qbus-output-types', adminOnly: true },
        { text: tr('Layout.menu_qbus_friendly_names', 'Qbus Friendly Names'), icon: <BulkNameIcon />, path: '/qbus-friendly-names', adminOnly: true },
        { text: tr('Layout.menu_qbus_monitor', 'Qbus Monitor'), icon: <MonitorIcon />, path: '/qbus-monitor', adminOnly: true },
        { text: tr('Layout.menu_qbus_prefixes', 'Domotica naamclassificatie'), icon: <PowerIcon />, path: '/qbus-prefixes', adminOnly: true },
        // Meldingsgeluiden (spec 20260831-domotica-meldingen-bewonerstablet, fase 2).
        // Geen `requiresPermission` en geen `adminOnly`: isItemVisible() leidt de
        // zichtbaarheid af uit PAGE_REGISTRY, waar `/notification-sounds` op
        // `resident_notifications.view` als read-verb staat. Een nav-pad dat de registry
        // niet kent, valt door als zichtbaar-voor-iedereen — vandaar de registratie.
        { text: tr('Layout.menu_notification_sounds', 'Meldingsgeluiden'), icon: <NotificationsIcon />, path: '/notification-sounds' },
        { text: tr('Layout.menu_telephony', 'Telephony'), icon: <PhoneIcon />, path: '/telephony', adminOnly: true },
        { text: tr('Layout.menu_baseip_management', 'BaseIP Management'), icon: <RouterIcon />, path: '/baseip-management', adminOnly: true },
        { text: tr('Layout.menu_indoor_mapping', 'Indoor Mapping'), icon: <MapIcon />, path: '/indoor-mapping', adminOnly: true },
        { text: tr('Layout.menu_eink_designer', 'E-Ink Designer'), icon: <EinkIcon />, path: '/eink-designer', adminOnly: true },
        { text: tr('Layout.menu_integration_management', 'Integration Management'), icon: <IntegrationIcon />, path: '/integration-management', adminOnly: true },
        { text: tr('Layout.menu_location_data_import', 'Location Data Import'), icon: <LocationsIcon />, path: '/location-data-import', adminOnly: true },
        // Gate comes from PAGE_REGISTRY (/location-phone-numbers -> locations.phone_numbers).
        { text: tr('Layout.menu_location_phone_numbers', 'Location phone numbers'), icon: <LocationsIcon />, path: '/location-phone-numbers' },
        { text: tr('Layout.menu_unit_audit', 'Unit Audit'), icon: <LocationsIcon />, path: '/unit-audit', adminOnly: true },
      ],
    },
    {
      id: 'organization',
      label: tr('Nav.organization', 'Organization'),
      icon: <OrganizationIcon />,
      defaultOpen: false,
      adminOnly: false,
      items: [
        { text: tr('Layout.menu_send_notifications', 'Send Notifications'), icon: <NotificationsActiveIcon />, path: '/notification-management', requiresPermission: canManageUsers },
        { text: tr('Layout.menu_company_management', 'Company Management'), icon: <BusinessIcon />, path: '/company-management', requiresPermission: companyManagementPermission },
        // The CUSTOMER's own organisation page (spec 20260819-registrationandonboarding
        // spec-02 LD-CM-SPLIT). Sits beside Company Management on purpose: the two look
        // similar and serve different audiences, so hiding one from the other's owner is
        // what keeps them apart.
        { text: tr('Layout.menu_organisation', 'Organisation'), icon: <BusinessIcon />, path: '/organisation', requiresPermission: organisationPermission },
        // Gate komt uit PAGE_REGISTRY (/branding -> branding.manage).
        { text: tr('Layout.menu_branding', 'Huisstijl'), icon: <BusinessIcon />, path: '/branding' },
        // Gate komt uit PAGE_REGISTRY (/persons/onboard -> persons.create).
        { text: tr('Layout.menu_resident_onboarding', 'Bewoner laten intrekken'), icon: <CategoryIcon />, path: '/persons/onboard' },
        // Gate komt uit PAGE_REGISTRY (/persons/offboard -> residents.offboard). Direct
        // naast de intrede, want de twee zijn één levenscyclus -- en zolang uittreden geen
        // eigen ingang had, was een harde delete het enige beschikbare gebaar.
        { text: tr('Layout.menu_resident_offboarding', 'Bewoner laten uittrekken'), icon: <CategoryIcon />, path: '/persons/offboard' },
        // Gate komt uit PAGE_REGISTRY (/checklist-templates -> resident_onboarding.templates).
        { text: tr('Layout.menu_checklist_templates', 'Intrede-checklist'), icon: <CategoryIcon />, path: '/checklist-templates' },
        // Het gesprek zelf. Staat vóór de formulieren: dit is wat een medewerker
        // dagelijks doet, de bouwer komt er zelden.
        { text: tr('Layout.menu_intakes', 'Intakegesprekken'), icon: <CategoryIcon />, path: '/intake' },
        // Gate komt uit PAGE_REGISTRY (/intake-forms -> intake.templates).
        { text: tr('Layout.menu_intake_forms', 'Intakeformulieren'), icon: <CategoryIcon />, path: '/intake-forms' },
        // Gate komt uit PAGE_REGISTRY (/print-profiles -> intake.templates).
        { text: tr('Layout.menu_print_profiles', 'Afdrukken naar derden'), icon: <CategoryIcon />, path: '/print-profiles' },
        // Gate komt uit PAGE_REGISTRY (/candidate-cleanup -> persons.view).
        { text: tr('Layout.menu_candidate_cleanup', 'Kandidaten opruimen'), icon: <CategoryIcon />, path: '/candidate-cleanup' },
        { text: tr('Layout.menu_person_type_management', 'Person Type Management'), icon: <CategoryIcon />, path: '/person-type-management', requiresPermission: canManageUsers },
      ],
    },
    {
      id: 'system',
      label: tr('Nav.system', 'System'),
      icon: <SystemSectionIcon />,
      defaultOpen: false,
      adminOnly: false,
      items: [
        // Paid module entitlements. requiresPermission and NOT adminOnly: `modules.manage`
        // is PROTECTED, so the admin role does not inherit it and adminOnly would show the
        // item to every admin while the API refuses all of them.
        { text: tr('Layout.menu_modules_admin', 'Modules (CARO)'), icon: <AppsIcon />, path: '/admin/modules', requiresPermission: modulesManagePermission },
        // Customer onboarding queue. requiresPermission and NOT adminOnly, for exactly the
        // reason spelled out above: `onboarding.view` is PROTECTED, so the admin role does
        // not inherit it and adminOnly would show the item to every admin while the API
        // refuses all of them.
        { text: tr('Layout.menu_onboarding', 'Onboarding'), icon: <BusinessIcon />, path: '/admin/onboarding', requiresPermission: onboardingViewPermission },
        { text: tr('Layout.menu_my_modules', 'My modules'), icon: <AppsIcon />, path: '/settings/modules', requiresPermission: modulesViewPermission },
        { text: tr('Layout.menu_service_management', 'Service Management'), icon: <AppsIcon />, path: '/service-management', adminOnly: true },
        { text: tr('Layout.menu_monitoring', 'Monitoring'), icon: <MonitorIcon />, path: '/monitoring', adminOnly: true },
        // Observability feedback loop (20260807-observability-feedback-loop §7). Here rather
        // than in the `monitor` section: that one is day-to-day operations (alarms, tickets,
        // notifications), while this is a technical surface about our own defects.
        //
        // `requiresPermission`, not `adminOnly`: isItemVisible() reads adminOnly as
        // role === 'admin' exactly, and the role this page exists for is `developer` — the only
        // one holding monitoring.manage and therefore the only one who can press its button.
        { text: tr('Layout.menu_observability', 'Observability'), icon: <TroubleshootIcon />, path: '/observability', requiresPermission: monitoringViewPermission },
        { text: tr('Layout.menu_troubleshooting', 'Troubleshooting'), icon: <HelpIcon />, path: '/troubleshooting', requiresPermission: monitoringViewPermission },
        // Database server health (2026-09-18). requiresPermission carries the key AND the
        // company-1 check (computed in Layout.js as dbHealthPermission).
        { text: tr('Layout.menu_database_health', 'Database health'), icon: <DatabaseIcon />, path: '/database-health', requiresPermission: dbHealthPermission },
        { text: tr('Layout.menu_hub_status', 'Hub Status'), icon: <MonitorIcon />, path: '/hub-status', adminOnly: true },
        { text: tr('Layout.menu_hub_location_status', 'Hub Location Status'), icon: <MonitorIcon />, path: '/hub-location-status', adminOnly: true },
        { text: tr('Layout.menu_developer_info', 'Developer Info'), icon: <CodeIcon />, path: '/developer-info', adminOnly: true },
        // Knowledge Base — reading is ungated (all roles); the authoring
        // console sub-link is gated on knowledge_base.manage (spec §8.2).
        { text: tr('Layout.menu_knowledge_base', 'Knowledge Base'), icon: <HelpIcon />, path: '/help' },
        { text: tr('Layout.menu_kb_admin', 'KB Authoring'), icon: <HelpIcon />, path: '/help/admin', requiresPermission: knowledgeBaseManagePermission },
        // Portal Ideabox (spec 20260817-portal-ideabox §8). Beside the Knowledge
        // Base: both are "help me with the product" surfaces. `ideasPermission` is
        // `ideas.view`, a role default for every portal role — the Triage tab gates
        // itself on the PROTECTED `ideas.triage` inside the page.
        { text: tr('Layout.menu_ideas', 'Ideas'), icon: <LightbulbIcon />, path: '/ideas', requiresPermission: ideasPermission },
      ],
    },
  ];
}

// Map old `menuOpenGroups` keys to new section ids so users who had
// collapsed/expanded particular groups don't lose their state.
export const OLD_TO_NEW_GROUP_KEY = {
  'management': 'organization',
  'device-management': 'hardware',
  'integrations': 'automation',
  'system': 'system',
};

export const NAV_FLAG_KEY = 'caro.portal.newNav';
